import { Dropdown, DropdownRef, Input, Menu } from '@recruit-robin/robin-theme/web-components';
import { useCallback, useMemo, useRef } from 'react';

import { toJobListItemViewModel, getJobIdentifier } from '../../model';

import { JobList } from '@/entities/job';
import type { JobListItemViewModel } from '@/entities/job';
import { useDictionary } from '@/shared/lib/i18n';
import { useStyles } from './JobImprovedSelect.styles';
import { JobImprovedSelectEndAdornment } from './JobImprovedSelectEndAdornment';
import type { JobImprovedSelectViewProps } from './JobImprovedSelect.types';

export const JobImprovedSelectView = ({
  search,
  onSearchChange,
  selectedJob,
  onSelectJob,
  jobList,
  isJobListLoading = false,
  searchPlaceholder,
  onEditJob,
}: JobImprovedSelectViewProps) => {
  const dictionary = useDictionary();
  const classes = useStyles();
  const dropdownRef = useRef<DropdownRef>(null);

  const placeholder = searchPlaceholder ?? dictionary.searchVacancy;

  const items = useMemo(() => jobList.map(toJobListItemViewModel), [jobList]);
  const selectedId = selectedJob ? getJobIdentifier(selectedJob) : null;

  const handleSelect = useCallback(
    (item: JobListItemViewModel) => {
      const summary = jobList.find((s) => getJobIdentifier(s) === item.id) ?? null;
      onSelectJob(summary);
      dropdownRef.current?.close();
    },
    [jobList, onSelectJob],
  );

  const handleEditJobFromList = useCallback(
    (jobId: string) => {
      const job = jobList.find((s) => getJobIdentifier(s) === jobId);
      if (job) onEditJob?.(job);
    },
    [jobList, onEditJob],
  );

  const dropdownValueRenderer = useCallback(
    ({ isOpen }: { isOpen: boolean }) => (
      <Input
        disabled={isJobListLoading}
        endIcon={
          <JobImprovedSelectEndAdornment
            isOpen={isOpen}
            isJobListLoading={isJobListLoading}
            selectedJob={selectedJob}
            onEditJob={onEditJob}
          />
        }
        onInput={(event) => onSearchChange?.(event.currentTarget.value)}
        value={isOpen ? search : selectedJob ? selectedJob.name : ''}
        contained
        placeholder={placeholder}
      />
    ),
    [onSearchChange, search, selectedJob, placeholder, isJobListLoading, onEditJob],
  );

  return (
    <Dropdown
      placement="bottom"
      flipMenu={false}
      ref={dropdownRef}
      shouldUsePortal={false}
      css={classes.selectWrapper}
      renderMenu={
        <Menu.Root customCss={classes.select}>
          <JobList
            items={items}
            selectedId={selectedId}
            onSelect={handleSelect}
            onEditJob={onEditJob ? handleEditJobFromList : undefined}
          />
        </Menu.Root>
      }>
      {dropdownValueRenderer}
    </Dropdown>
  );
};
